import { logger } from '@rhg/libs/logger';
import nextjsDefaultLoader from 'next/dist/shared/lib/image-loader';
// eslint-disable-next-line no-restricted-imports
import type { ImageLoader, ImageLoaderProps } from 'next/image';

const calmLoader = ({ src, width }: ImageLoaderProps): string | undefined => {
    let parsedSrc;
    try {
        parsedSrc = new URL(src);
    } catch (err) {
        logger.error(
            'Failed to parse src on `next/image`, if using relative image it must start with a leading slash "/" or be an absolute URL (http:// or https://)',
            { src },
            err as Error
        );
        return undefined;
    }
    if (
        !['assets.calm.com', 'assets-prod-usw.calm.com'].includes(
            parsedSrc.hostname
        )
    ) {
        return undefined;
    }
    const pathnamePieces = parsedSrc.pathname.split('/');
    if (pathnamePieces.length === 3) {
        const transformPath = pathnamePieces[1];
        const srcWidthString = transformPath.split('x')[0];
        try {
            // the calm assets service will upsize images,
            // so we have to take the Math.min ourselves client-side
            const srcWidth = parseInt(srcWidthString, 10);
            pathnamePieces[1] = `${Math.min(srcWidth, width)}`;
        } catch {
            logger.warn(
                'Attempted to transform an unrecognized assets.calm.com url',
                { src }
            );
        }
    } else {
        pathnamePieces.splice(1, 0, `${width}`);
    }
    parsedSrc.pathname = pathnamePieces.join('/');
    return parsedSrc.toString();
};

const loader: ImageLoader = ({ src, width: _width, quality }) => {
    const width = Math.floor(_width);

    if (
        src.includes('assets.calm.com') ||
        src.includes('assets-prod-usw.calm.com')
    ) {
        const calmUrl = calmLoader({ src, width, quality });
        if (calmUrl) {
            return calmUrl;
        }
    }

    // Fall back to the default next loader
    // Note: we have to hard-code the config here (from next.config.js)
    // as it's not available at runtime
    const config = {
        remotePatterns: [
            {
                protocol: 'https' as const,
                hostname: '**.calm.com',
            },
            {
                protocol: 'https' as const,
                hostname: '**.calmhealth.com',
            },
            {
                protocol: 'https' as const,
                hostname: 'res.cloudinary.com',
                pathname: '/calm-com/**',
            },
        ],
        domains: [],
        loader: 'custom' as const,
        loaderFile: './src/utils/images/imageLoader.ts',
        path: '/_next/image',
    };

    const safeSrc = /^\//.test(src) || /^http/.test(src) ? src : `/${src}`;
    return nextjsDefaultLoader({
        config,
        src: safeSrc,
        width: _width,
        quality,
    });
};

export default loader;

export const imageMatches = (input: string, expected: string): boolean => {
    const transformed = loader({ src: input, width: 3840 });
    return transformed === expected;
};
